import { TranslationContext } from "@contexts";
import { PageLoader } from "@features/common/ui/PageLoader";
import { MyRequestsNavLabel } from "@features/my-requests/components/MyRequestsNavLabel";
import { useUnreadReceivedOrders } from "@features/my-requests/useUnreadReceivedOrders";
import { useQueryAppConfiguration } from "@hooks/appConfiguration/useQueryAppConfiguration";
import { useMarketplaceIdentity } from "@hooks/useMarketplaceIdentity";
import { InlineIcon } from "@iconify/react";
import { useKeycloak } from "@react-keycloak/web";
import styleVariables from "@style_setup";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { Link } from "react-router-dom";
import { CartIconComp } from "./CartIconComp";
import { mainMenuItems } from "./helpers";

const styles = createUseStyles({
  MainMenuModal: {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100vw",
    height: "100vh",
    zIndex: "9999",
  },
  overlay: {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,.8)",
    zIndex: "0",
  },
  contentContr: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -60%)",
    textAlign: "end",
    width: "75vw",
  },
  content: {
    background: "white",
    padding: "2rem",
    display: "grid",
    gap: "1.7rem",
    width: "98%",
    justifyItems: "center",
    "& hr": {
      width: "50%",
      margin: "0",
    },
  },
  closeX: {
    color: "white",
    fontSize: "xxx-large",
  },
  menuItem: {
    color: "black",
    textDecoration: "none",
  },
  lockedMenuItem: {
    color: "#94a3b8",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    cursor: "not-allowed",
    background: "transparent",
    border: "none",
    font: "inherit",
  },
  greeting: {
    color: "#334155",
    fontSize: "1.1rem",
    fontWeight: 600,
    margin: 0,
    textAlign: "center",
  },
  [`@media ${styleVariables.media.smallscreen}`]: {},
});
export const MainMenuModal = ({ setShowModal = () => {} }) => {
  const { t } = useContext(TranslationContext);

  const classes = styles({});

  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak.authenticated;
  const isSuperAdmin = keycloak.hasRealmRole("super-admin");

  const { data: configObj, isLoading } = useQueryAppConfiguration();
  const identity = useMarketplaceIdentity();

  const profileGreeting = identity.displayName
    ? t("profileGreeting", { name: identity.displayName })
    : t("profileGreetingGeneric");

  const { hasUnread: hasUnreadMyRequests } = useUnreadReceivedOrders();

  const myRequestsMenuTitle = (
    <MyRequestsNavLabel showNotification={hasUnreadMyRequests} ringClassName="ring-white">
      {t("myRequests")}
    </MyRequestsNavLabel>
  );

  function closeModal() {
    setShowModal(false);
  }

  const logout = () => {
    keycloak.logout({ redirectUri: `${window.location.origin}/` });
    closeModal();
  };

  return (
    <div className={classes.MainMenuModal}>
      <div className={classes.overlay} />
      <div className={classes.contentContr}>
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            <InlineIcon
              icon="flowbite:close-solid"
              className={classes.closeX}
              onClick={closeModal}
            />
            <div className={classes.content}>
              {isAuthenticated && (
                <>
                  <p className={classes.greeting} data-testid="mobileMenuGreeting">
                    {profileGreeting}
                  </p>
                  {isSuperAdmin && (
                    <Link to="/back-office" className={classes.navbarItem}>
                      {t("backOffice")}
                    </Link>
                  )}
                  <Link
                    to="/products"
                    onClick={closeModal}
                    className={classes.menuItem}
                    data-testid="allProductsNavMobile"
                  >
                    {t("allProducts")}
                  </Link>
                  {mainMenuItems({
                    myRequestsTitle: myRequestsMenuTitle,
                    lockMyAssets: !identity.showSellerNav,
                  }).map((item) => (
                    <React.Fragment key={item.arg}>
                      {!item.arg.includes("cart") && item.locked && (
                        <button
                          type="button"
                          className={classes.lockedMenuItem}
                          disabled
                          aria-disabled="true"
                        >
                          {typeof item.title === "string"
                            ? t(item.title)
                            : item.title}
                          <InlineIcon icon="fa6-solid:lock" aria-hidden />
                        </button>
                      )}
                      {!item.arg.includes("cart") && !item.locked && (
                        <Link
                          to={item.arg}
                          onClick={closeModal}
                          className={classes.menuItem}
                        >
                          {typeof item.title === "string"
                            ? t(item.title)
                            : item.title}
                        </Link>
                      )}

                      {configObj.hasCart && item.arg.includes("cart") && (
                        <Link
                          to={item.arg}
                          onClick={closeModal}
                          className={classes.menuItem}
                        >
                          <CartIconComp />
                        </Link>
                      )}
                    </React.Fragment>
                  ))}
                  <hr />
                  <button type="button" onClick={logout}>
                    {t("logout")}
                  </button>
                </>
              )}
              {/* {configObj.hasMultiLanguage && (
          )} */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
MainMenuModal.propTypes = {
  setShowModal: PropTypes.func.isRequired,
};
